import {
  EMPLOYEE_STAFF_LEVEL_OPTIONS,
  type OrganizationEmployeeTrainingSummaryData,
  type OrganizationEmployeeTrainingSummaryItem,
} from '../types/employee-training.type';
import type { UserSchema } from '../types/auth.type';

export type OrganizationUserProfile = Pick<
  UserSchema,
  'id' | 'sub' | 'username' | 'phone' | 'full_name' | 'data'
>;

export const organizationUsersQueryKey = (organizationId: string) =>
  ['organization-users', organizationId] as const;

export const EMPTY_CELL = '—';

export function parseTrainingHours(value: string | number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function trimOrNull(value: string | null | undefined): string | null {
  const t = value?.trim();
  return t ? t : null;
}

function coalesceName(
  summaryVal: string | null | undefined,
  profileVal: string | null | undefined,
): string | null {
  return trimOrNull(summaryVal) ?? trimOrNull(profileVal);
}

export function getOrganizationUserId(user: OrganizationUserProfile): string | null {
  return trimOrNull(user.id) ?? trimOrNull(user.sub);
}

export function buildOrganizationUserProfileMap(
  users: OrganizationUserProfile[],
): Map<string, OrganizationUserProfile> {
  const map = new Map<string, OrganizationUserProfile>();
  for (const user of users) {
    const id = getOrganizationUserId(user);
    if (id) map.set(id, user);
  }
  return map;
}

export function enrichTrainingSummaryRow(
  row: OrganizationEmployeeTrainingSummaryItem,
  profile?: OrganizationUserProfile | null,
): OrganizationEmployeeTrainingSummaryItem {
  if (!profile) return row;

  const profileData = profile.data;
  const mergedData: OrganizationEmployeeTrainingSummaryData = {
    ...(row.data ?? {}),
    ...(profileData ?? {}),
    first_name: coalesceName(row.data?.first_name, profileData?.first_name),
    last_name: coalesceName(row.data?.last_name, profileData?.last_name),
    full_name: coalesceName(row.data?.full_name, profile.full_name ?? profileData?.full_name),
  };

  return {
    ...row,
    first_name: coalesceName(row.first_name, profileData?.first_name),
    last_name: coalesceName(row.last_name, profileData?.last_name),
    full_name: coalesceName(row.full_name, profile.full_name ?? profileData?.full_name),
    username: coalesceName(row.username, profile.username) ?? row.username,
    phone: coalesceName(row.phone, profile.phone) ?? row.phone,
    data: mergedData,
  };
}

export function enrichTrainingSummaryList(
  rows: OrganizationEmployeeTrainingSummaryItem[],
  users: OrganizationUserProfile[],
): OrganizationEmployeeTrainingSummaryItem[] {
  if (!users.length) return rows;
  const profileByUserId = buildOrganizationUserProfileMap(users);
  return rows.map((row) => enrichTrainingSummaryRow(row, profileByUserId.get(row.user_id)));
}

function resolveFirstName(row: OrganizationEmployeeTrainingSummaryItem): string | null {
  return trimOrNull(row.first_name) ?? trimOrNull(row.data?.first_name);
}

function resolveLastName(row: OrganizationEmployeeTrainingSummaryItem): string | null {
  return trimOrNull(row.last_name) ?? trimOrNull(row.data?.last_name);
}

function resolveFullName(row: OrganizationEmployeeTrainingSummaryItem): string | null {
  return trimOrNull(row.full_name) ?? trimOrNull(row.data?.full_name);
}

/** Full name column only: no username/phone fallback. */
export function fullNameForTable(row: OrganizationEmployeeTrainingSummaryItem): string {
  const full = resolveFullName(row);
  if (full) return full;
  const parts = [resolveFirstName(row), resolveLastName(row)].filter(Boolean) as string[];
  if (parts.length) return parts.join(' ');
  return EMPTY_CELL;
}

/** Actions, dialogs, and detail header — includes username/phone fallback. */
export function employeeDisplayName(row: OrganizationEmployeeTrainingSummaryItem): string {
  const tableName = fullNameForTable(row);
  if (tableName !== EMPTY_CELL) return tableName;
  return row.username?.trim() || row.phone?.trim() || row.user_id;
}

export function cellOrDash(value: string | null | undefined): string {
  const t = value?.trim();
  return t ? t : EMPTY_CELL;
}

export function isPlaceholderCell(value: string | null | undefined): boolean {
  const t = value?.trim();
  return !t || t === EMPTY_CELL;
}

/** Center placeholder (—); otherwise use column data alignment. */
export function cellAlignForValue(
  value: string,
  dataAlign: 'left' | 'right' | 'center',
): 'left' | 'right' | 'center' {
  return isPlaceholderCell(value) ? 'center' : dataAlign;
}

const EMPLOYEE_STAFF_LEVEL_SET = new Set<string>(EMPLOYEE_STAFF_LEVEL_OPTIONS);

/** Keeps only selections that match backend `EmployeeStaffLevel` values. */
export function pruneEmployeeLevelFilter(selected: string[]): string[] {
  return selected.filter((level) => EMPLOYEE_STAFF_LEVEL_SET.has(level));
}

/** Empty selection = no level constraint (show all rows). */
export function matchesEmployeeLevelFilter(
  row: OrganizationEmployeeTrainingSummaryItem,
  selectedLevels: string[],
): boolean {
  if (selectedLevels.length === 0) return true;
  const level = trimOrNull(row.employee_level);
  if (!level) return false;
  return selectedLevels.includes(level);
}

export function overviewRowSearchHay(row: OrganizationEmployeeTrainingSummaryItem): string {
  return [
    fullNameForTable(row),
    employeeDisplayName(row),
    row.full_name,
    row.first_name,
    row.last_name,
    row.data?.full_name,
    row.data?.first_name,
    row.data?.last_name,
    row.username,
    row.phone,
    row.employee_level,
    row.user_id,
    String(parseTrainingHours(row.training_hours_total)),
    String(row.session_count),
    row.last_session_date ?? '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}
